import { useState, useEffect, useCallback } from "react";
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  getDeletedSegmentations,
  restoreSegmentation,
} from "../../services/container";
import { showErrorToast, showSuccessToast } from "../../utils/errorHandler";
import CloseIcon from "@mui/icons-material/Close";

const RestoreDeletedSegmentations = ({
  isOpen,
  toggle,
  blobName,
  captureTime,
  onRestore,
}) => {
  const { t } = useTranslation();
  const [deletedSegmentations, setDeletedSegmentations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const fetchDeletedSegmentations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getDeletedSegmentations({ blobName });
      if (response.data?.deletedSegmentations) {
        setDeletedSegmentations(response.data.deletedSegmentations);
      }
    } catch (error) {
      console.error("Error fetching deleted segmentations:", error);
      showErrorToast(error, t("Error loading deleted segmentations"));
    } finally {
      setLoading(false);
    }
  }, [blobName, t]);

  useEffect(() => {
    if (isOpen && blobName) {
      fetchDeletedSegmentations();
    }
  }, [isOpen, blobName, fetchDeletedSegmentations]);

  const handleRestore = async (segmentationId) => {
    try {
      setRestoringId(segmentationId);
      await restoreSegmentation({
        blobName,
        segmentationId,
        captureTime,
      });

      setDeletedSegmentations((prev) =>
        prev.filter((seg) => seg.id !== segmentationId),
      );

      showSuccessToast(t("Segmentation restored successfully"), t);
      if (onRestore) {
        onRestore();
      }
    } catch (error) {
      console.error("Error restoring segmentation:", error);
      showErrorToast(error, t("Error restoring segmentation"));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader>
        {t("Restore Deleted Segmentations")}
        <CloseIcon
          style={{
            color: "var(--gray)",
            cursor: "pointer",
          }}
          onClick={toggle}
        />
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-4">{t("Loading...")}</div>
        ) : deletedSegmentations.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            {t("No deleted segmentations found")}
          </div>
        ) : (
          <div className="space-y-2">
            {deletedSegmentations.map((seg) => (
              <div
                key={seg.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium">
                    {seg.cls || seg.reviewCls || t("Unknown")}
                  </div>
                  {seg.deletedAt && (
                    <div className="text-sm text-gray-500">
                      {t("Deleted at")}:{" "}
                      {new Date(seg.deletedAt).toLocaleString()}
                    </div>
                  )}
                </div>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => handleRestore(seg.id)}
                  disabled={restoringId === seg.id}
                >
                  {restoringId === seg.id ? t("Restoring...") : t("Restore")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("Close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RestoreDeletedSegmentations;
